import '@uirouter/angularjs';
import assign from 'lodash/assign';
import each from 'lodash/each';
import find from 'lodash/find';
import some from 'lodash/some';
import remove from 'lodash/remove';

//eslint-disable-next-line no-unused-vars
export class GridController {
    constructor(
        $window,
        $location,
        $state,
        $translate,
        $mdToast,
        $mdDialog,
        $timeout,
        gridService,
        loadingModalService,
        endpoint,
        gtmid
    ) {
        assign(this, {
            _endpoint: endpoint,
            $window,
            $state,
            $mdToast,
            $mdDialog,
            $translate,
            $timeout,
            gridService,
            loadingModalService,
            filter: $location.search().filter
        });

        this.grid = {
            options: this.gridService
                .getDefaultOptions(this._endpoint, this.onDataResponse.bind(this), this.filter, gtmid),
            items: [],
            selected: [],
            allSelected: false
        };

        if (this.filter) {
            this.filter = this.filter && this.filter !== 'All' ? `state eq "${this.filter}"` : null;
            this.gridService
                .updateGridCallbacks(this._endpoint, this.filter, this.grid.options, this.onDataResponse.bind(this));
        }
    }

    setFilter(newFilter) {
        this.gridService
            .updateGridCallbacks(this._endpoint, newFilter, this.grid.options, this.onDataResponse.bind(this));

        // fetch new items and render to grid
        this.$timeout(() => {
            //reset grid back to page 1
            this.grid.items = [];
            this.grid.options.paging.currentPage = 1;
            this.grid.options.sorting.onSortChange(null, null, this.grid.options.sorting.sort);
        });
    }

    onDataResponse(response, type) {
        this.grid.options.isLoading = false;
        this.grid.options.scrollLoader = false;

        // type for customer/product lists is undefined
        this.grid.items = type === 'page'
            ? this.grid.items.concat(response.items)
            : this.grid.items = response.items;

        this.grid.options.paging.totalItemCount = response.totalCount || response.total;

        each(this.grid.selected, (selected) => {
            var item = find(this.grid.items, {
                id: selected.id
            });
            if (item) {
                item.selected = true;
            }
        });

        this.checkAll();
    }

    areAllSelected() {
        if (this.grid.items.length) {
            return !some(this.grid.items, (item) => {
                return !item.selected;
            });
        }
    }

    select(row) {
        if (some(this.grid.selected, { id: row.id })) {
            remove(this.grid.selected, {
                id: row.id
            });
            row.selected = false;
        } else {
            this.grid.selected.push(row);
            row.selected = true;
        }

        this.checkAll();

        if (this.$window.dataLayer) {
            this.$window.dataLayer.push({
                event: `admin_${this.$state.current.name}-gridSelectRow`
            });
        }
    }

    selectAll() {
        if (!this.areAllSelected()) {
            each(this.grid.items, (item) => {
                if (!some(this.grid.selected, { id: item.id })) {
                    item.selected = true;
                    this.grid.selected.push(item);
                }
            });
            this.grid.allSelected = true;
        } else {
            each(this.grid.items, (item) => {
                item.selected = false;
                remove(this.grid.selected, {
                    id: item.id
                });
            });
            this.grid.allSelected = false;
        }

        if (this.$window.dataLayer) {
            this.$window.dataLayer.push({
                event: `admin_${this.$state.current.name}-gridSelectAll`
            });
        }
    }

    clearAll() {
        while (this.grid.selected.length > 0) {
            var item = this.grid.selected.shift();
            item.selected = false;
        }

        this.grid.allSelected = false;
    }

    checkAll() {
        this.grid.allSelected = this.areAllSelected();
    }

    openRow(row, location, params = {}) {
        if (this.hasRowLoading()) return;
        row.loading = true;
        this.$state.go(location, { id: row.id, ...params });
    }

    hasRowLoading() {
        return some(this.grid.items, ['loading', true]);
    }

    remove(row, type, fromBulk, translationRoot) {
        if (this.$window.dataLayer && !fromBulk) {
            this.$window.dataLayer.push({
                event: `admin_${this.$state.current.name}-gridDeleteRow`
            });
        }
        if (!translationRoot) translationRoot = type.toUpperCase();

        return this.openDeleteModal(row, translationRoot)
            .then(() => {
                return this.gridService.remove(this._endpoint, row);
            })
            .then((response) => {
                remove(this.grid.items, {
                    id: row.id
                });
                this.sendDeleteToast(row);
                let options = this.grid.options;
                this.grid.selected.length = 0;
                options.sorting.onSortChange(options.paging.currentPage, options.totalItemCount, options.sorting.sort);

                return response;
            });
    }

    removeBulk(type, translationRoot) {
        if (!translationRoot) translationRoot = type.toUpperCase();
        if (this.grid.selected.length === 1) {
            return this.remove(this.grid.selected[0], type, true, translationRoot);
        }

        return this.openDeleteModal(this.grid.selected, translationRoot)
            .then(() => {
                remove(this.grid.items, (item) => {
                    return some(this.grid.selected, {
                        id: item.id
                    });
                });

                this.loadingModalService.show();
                return this.gridService.removeAll(this._endpoint, this.grid.selected);
            })
            .then((response) => {
                this.sendBulkDeleteToast(this.grid.selected.length, translationRoot);
                this.grid.selected.length = 0;
                let options = this.grid.options;
                options.sorting.onSortChange(options.paging.currentPage, options.totalItemCount, options.sorting.sort);
                this.checkAll();

                return response;
            }).finally(() => {
                this.loadingModalService.hide();
            });
    }

    sendDeleteToast(item) {
        this.$translate('TOAST.SUCCESSFULLYREMOVED', { NAME: item.name }).then((message) => {
            this.$mdToast.show(
                this.$mdToast.simple()
                    .textContent(message)
                    .position('top right')
                    .hideDelay(5000)
            );
        });
    }

    sendBulkDeleteToast(count, type) {
        this.$translate(`${type}.PLURAL`).then((translateType) => {
            this.$translate('TOAST.MULTIREMOVE', { COUNT: count, TYPE: translateType }).then((message) => {
                this.$mdToast.show(
                    this.$mdToast.simple()
                        .textContent(message)
                        .position('top right')
                        .hideDelay(5000)
                );
            });
        });
    }

    openDeleteModal(obj, type) {
        let dialogOptions = {
            clickOutsideToClose: true,
            template: `<delete-modal
            item-count="${Array.isArray(obj) ? obj.length : 1}"
            item-type="'${type}'"
            is-detail="false"></delete-modal>`
        };

        return this.$mdDialog.show(dialogOptions);
    }
}



// WEBPACK FOOTER //
// ./src/app/components/grid/grid.controller.js